export const CM_PER_FOOT = 30.48
export const CM_PER_INCH = 2.54
export const LBS_PER_KG = 2.20462

/**
 * Converts centimeters to feet and inches (both integers).
 * Uses Math.floor for ft and Math.round for in.
 * Handles the edge case where inches rounds to 12 by incrementing feet.
 */
export function cmToFeetInches(cm: number): { ft: number; in: number } {
  if (cm <= 0) return { ft: 0, in: 0 }

  const totalInches = cm / CM_PER_INCH
  let ft = Math.floor(totalInches / 12)
  let inches = Math.round(totalInches - ft * 12)

  if (inches === 12) {
    ft += 1
    inches = 0
  }

  return { ft, in: inches }
}

/**
 * Converts feet and inches to centimeters.
 * Returns a float - NEVER rounded.
 */
export function feetInchesToCm(ft: number, inches: number): number {
  return ft * CM_PER_FOOT + inches * CM_PER_INCH
}

/**
 * Converts centimeters to inches — a plain, sign-preserving straight-line
 * conversion. Unlike `cmToFeetInches` (which assumes an always-positive
 * height and floors negative/zero input to `{ft:0, in:0}`), this is for a
 * single-axis measurement that can be negative (e.g. a body-measurement
 * reduction). Returns a float - NEVER rounded; rounding is the formatter's job.
 */
export function cmToInches(cm: number): number {
  return cm / CM_PER_INCH
}

/**
 * Converts kilograms to pounds.
 * Returns a rounded integer.
 */
export function kgToLbs(kg: number): number {
  if (kg <= 0) return 0
  return Math.round(kg * LBS_PER_KG)
}

/**
 * Converts pounds to kilograms.
 * Returns a float - NEVER rounded.
 */
export function lbsToKg(lbs: number): number {
  if (lbs <= 0) return 0
  return lbs / LBS_PER_KG
}

/**
 * Rounds a value to the nearest integer for UI display.
 * Store values may be floats (e.g. after imperial->metric conversion),
 * but UI always shows integers.
 */
export function roundForDisplay(value: number): number {
  return Math.round(value)
}
